/**
 * @fileoverview Organism: the form that adds or edits a habit.
 * @module components/organisms/HabitForm
 */

import { useState, type FormEvent } from "react"

import { Button, Input, Select } from "@/components/atoms"
import { PREDEFINED_CATEGORIES } from "@/lib/domain/categories"
import { formatMilestone, MILESTONES } from "@/lib/domain/milestones"
import { WEEKDAY_LABELS } from "@/lib/domain/schedule"
import type { HabitDraft, HabitSchedule, Weekday } from "@/types/types"

import "./HabitForm.css"

/** Props accepted by {@link HabitForm}. */
export interface HabitFormProps {
	/** Called with a valid draft when the form is submitted. */
	readonly onSubmit: (draft: HabitDraft) => void
	/** Pre-fills the fields for editing; omit for a blank "add" form. */
	readonly initialValue?: HabitDraft
	/** Submit button label. Defaults to "Créer l'habitude". */
	readonly submitLabel?: string
}

/** Field-level validation messages, keyed by field name. */
type FieldErrors = Partial<Record<"name" | "days" | "everyNDays", string>>

/** Editable form state: everything is a string/array until it is parsed. */
interface FormValues {
	name: string
	scheduleType: HabitSchedule["type"]
	weeklyDays: Weekday[]
	everyNDays: string
	goalReps: string
	category: string
}

/** Blank form state: daily, no category, the default 66-rep goal. */
const EMPTY: FormValues = {
	name: "",
	scheduleType: "daily",
	weeklyDays: [],
	everyNDays: "2",
	goalReps: "66",
	category: "",
}

/**
 * Converts a draft into the values the form fields edit.
 *
 * @param draft - The draft to pre-fill from, or undefined for a blank form.
 * @returns Form values seeded from the draft.
 */
function toFormValues(draft: HabitDraft | undefined): FormValues {
	if (draft === undefined) return EMPTY

	return {
		name: draft.name,
		scheduleType: draft.schedule.type,
		weeklyDays:
			draft.schedule.type === "weekly" ? [...draft.schedule.days] : [],
		everyNDays:
			draft.schedule.type === "interval"
				? String(draft.schedule.everyNDays)
				: EMPTY.everyNDays,
		goalReps: String(draft.goalReps),
		category: draft.category ?? "",
	}
}

/**
 * Form adding or editing a habit: name, recurring schedule, and a goal
 * picked from the fixed milestone tiers.
 *
 * @param props - Submit handler receiving a validated draft, plus optional
 *   pre-filled values when the form is used to edit rather than add.
 * @returns The rendered form.
 */
export function HabitForm({
	onSubmit,
	initialValue,
	submitLabel = "Créer l'habitude",
}: HabitFormProps) {
	const [values, setValues] = useState(() => toFormValues(initialValue))
	const [errors, setErrors] = useState<FieldErrors>({})

	// A habit saved before this list existed, or imported, may carry a
	// category outside it: keep it selectable instead of dropping it silently.
	const customCategory =
		values.category !== "" &&
		!PREDEFINED_CATEGORIES.includes(
			values.category as (typeof PREDEFINED_CATEGORIES)[number]
		)
			? values.category
			: null

	const toggleWeekday = (day: Weekday) => {
		setValues(previous => ({
			...previous,
			weeklyDays: previous.weeklyDays.includes(day)
				? previous.weeklyDays.filter(value => value !== day)
				: [...previous.weeklyDays, day].sort((a, b) => a - b),
		}))
	}

	/**
	 * Validates the form and hands a draft up on success.
	 *
	 * @param event - The submit event.
	 */
	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		const nextErrors: FieldErrors = {}

		if (values.name.trim() === "") {
			nextErrors.name = "Donnez un nom à cette habitude."
		}

		const everyNDays = Number(values.everyNDays)
		if (
			values.scheduleType === "interval" &&
			(!Number.isFinite(everyNDays) || everyNDays < 1)
		) {
			nextErrors.everyNDays = "Au moins un jour entre deux répétitions."
		}

		if (
			values.scheduleType === "weekly" &&
			values.weeklyDays.length === 0
		) {
			nextErrors.days = "Choisissez au moins un jour."
		}

		setErrors(nextErrors)
		if (Object.keys(nextErrors).length > 0) return

		const schedule: HabitSchedule =
			values.scheduleType === "weekly"
				? { type: "weekly", days: values.weeklyDays }
				: values.scheduleType === "interval"
					? { type: "interval", everyNDays: Math.round(everyNDays) }
					: { type: "daily" }

		onSubmit({
			name: values.name,
			schedule,
			goalReps: Number(values.goalReps),
			...(values.category.trim() === ""
				? {}
				: { category: values.category }),
		})

		// Editing keeps the modal open on the submitted values; only a fresh
		// "add" form clears itself for the next entry.
		if (initialValue === undefined) setValues(EMPTY)
	}

	return (
		<form className="habit-form" onSubmit={handleSubmit} noValidate>
			<Input
				label="Nom"
				value={values.name}
				placeholder="Pompes"
				onChange={event => {
					setValues({ ...values, name: event.target.value })
				}}
				{...(errors.name === undefined ? {} : { error: errors.name })}
			/>

			<Select
				label="Fréquence"
				value={values.scheduleType}
				onChange={event => {
					setValues({
						...values,
						scheduleType: event.target
							.value as HabitSchedule["type"],
					})
				}}
			>
				<option value="daily">Tous les jours</option>
				<option value="weekly">Certains jours de la semaine</option>
				<option value="interval">Tous les N jours</option>
			</Select>

			{values.scheduleType === "weekly" && (
				<div className="habit-form__weekdays">
					<span className="habit-form__weekdays-label">Jours</span>
					<div className="habit-form__weekdays-group" role="group">
						{WEEKDAY_LABELS.map((label, day) => (
							<button
								key={label}
								type="button"
								className={`habit-form__weekday${
									values.weeklyDays.includes(day as Weekday)
										? " habit-form__weekday--selected"
										: ""
								}`}
								aria-pressed={values.weeklyDays.includes(
									day as Weekday
								)}
								onClick={() => {
									toggleWeekday(day as Weekday)
								}}
							>
								{label}
							</button>
						))}
					</div>
					{errors.days !== undefined && (
						<span className="field__error" role="alert">
							{errors.days}
						</span>
					)}
				</div>
			)}

			{values.scheduleType === "interval" && (
				<Input
					label="Tous les combien de jours"
					value={values.everyNDays}
					inputMode="numeric"
					placeholder="3"
					onChange={event => {
						setValues({ ...values, everyNDays: event.target.value })
					}}
					{...(errors.everyNDays === undefined
						? {}
						: { error: errors.everyNDays })}
				/>
			)}

			<Select
				label="Objectif (répétitions)"
				value={values.goalReps}
				onChange={event => {
					setValues({ ...values, goalReps: event.target.value })
				}}
			>
				{MILESTONES.map(tier => (
					<option key={tier} value={tier}>
						{formatMilestone(tier)}
					</option>
				))}
			</Select>

			<Select
				label="Catégorie"
				value={values.category}
				onChange={event => {
					setValues({ ...values, category: event.target.value })
				}}
			>
				<option value="">Sans catégorie</option>
				{customCategory !== null && (
					<option value={customCategory}>{customCategory}</option>
				)}
				{PREDEFINED_CATEGORIES.map(category => (
					<option key={category} value={category}>
						{category}
					</option>
				))}
			</Select>

			<div>
				<Button type="submit">{submitLabel}</Button>
			</div>
		</form>
	)
}
